import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchWebinars } from '@/features/webinar/webinarThunks';
import { clearCurrentWebinar } from '@/features/webinar/webinarSlice';
import CreateAndListWebinars from './CreateAndListWebinars';
import { useColumns, type WebinarListItem } from './columns';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import Title from '@/components/ui/Title';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import WebinarSessionsTable from './WebinarsSessionsTable';
import { useTranslation } from 'react-i18next';
import { getFormattedDate } from '@/lib/utils';
import type { WebinarSessionDetail } from '@/features/webinar/types';

export default function ProgramSection() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('webinars');
  const columns = useColumns();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const workspaceTimezone = useAppSelector((state) => state.workspace.currentWorkspace?.timezone);
  const { webinars, loading, webinarsTotalPages } = useAppSelector((state) => state.webinar);
  const [pageNumber, setPageNumber] = useState(0);
  const totalPages = Math.max(1, webinarsTotalPages);

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchWebinars({ workspaceId, page: pageNumber, size: 10 }));
    }
  }, [dispatch, workspaceId, pageNumber]);

  const data: WebinarListItem[] = webinars.map((webinar) => {
    const sessionAt = webinar.nextSessionAt || webinar.lastSessionAt;
    const sessionTimezone =
      (webinar.nextSessionAt ? webinar.nextSessionTimezone : webinar.lastSessionTimezone) ||
      workspaceTimezone;
    const sessionId = webinar.nextSessionId ?? webinar.lastSessionId ?? 0;
    const nextSessionId = webinar.nextSessionId ?? undefined;
    const webinarType = webinar.type ?? 'LIVE';
    const sessionDetail: WebinarSessionDetail | undefined =
      webinar.status !== 'DRAFT' && sessionId
        ? ({
            id: sessionId,
            identifier: webinar.nextSessionIdentifier ?? webinar.lastSessionIdentifier ?? '',
            webinarId: webinar.id,
            workspaceId: webinar.workspaceId,
            webinarTitle: webinar.title,
            webinarDescription: webinar.description,
            webinarShortDescription: webinar.shortDescription,
            sessionType: webinarType,
            utcPlannedDate: sessionAt ?? '',
            presenters: (webinar as any).presenters ?? [],
            speakers: webinar.speakers ?? [],
            moderators: webinar.moderators ?? [],
            status: webinar.status ?? '',
            timezone: sessionTimezone,
            registrationUrl: webinar.registrationUrl,
            replayRegistrationUrl: webinar.replayRegistrationUrl,
          } satisfies WebinarSessionDetail)
        : undefined;

    const formattedDate =
      webinar.status !== 'DRAFT' && sessionAt
        ? (() => {
            return getFormattedDate(sessionAt, sessionTimezone);
          })()
        : '';

    return {
      id: String(webinar.id),
      title: webinar.title,
      type: webinarType,
      date: formattedDate,
      attendee: webinar?.participantsCount ?? 0,
      moderator: webinar.moderators ?? [],
      status: webinar.status ?? '',
      webinarId: webinar.id,
      sessionId: sessionId,
      nextSessionId,
      sessionDetail,
      registrationUrl: webinar.registrationUrl,
      accessCodeEnabled: webinar.accessCodeEnabled,
      evergreenTestAvailable: webinar.evergreenTestAvailable,
    };
  });

  const isLoading = loading;

  if (isLoading) {
    return (
      <div className="space-y-8">
        <CreateAndListWebinars />
        <div className="space-y-8">
          <Title level={2}>{t('sessionsTable.title')}</Title>
          <div className="overflow-hidden border border-[#FBFBFB] rounded-2xl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Skeleton className="h-4 w-4" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-32" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-40" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-24" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-4" />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-5 w-16" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-56" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-8" />
                    </TableCell>
                    <TableCell>
                      <div className="flex -space-x-2">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <Skeleton className="h-8 w-8 rounded-full" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-9 w-24" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <CreateAndListWebinars />
      <WebinarSessionsTable
        columns={columns}
        data={data}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        totalPages={totalPages}
        onRowClick={(row: WebinarListItem) => {
          dispatch(clearCurrentWebinar());
          if (row.status.includes('DRAFT')) {
            navigate(`/create-webinar/configuration/${row.webinarId}`);
          } else {
            navigate(`/webinars/${row.webinarId}`);
          }
        }}
      />
    </div>
  );
}
